import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, ChevronLeft, MessageCircle } from 'lucide-react';
import { messagesAPI } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';

const Messages = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchConversations();
    }, []);

    const fetchConversations = async () => {
        try {
            const response = await messagesAPI.getConversations();
            setConversations(response.data);
        } catch (error) {
            console.error('Failed to fetch conversations:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredConversations = conversations.filter(conv =>
        conv.user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (conv.user.display_name && conv.user.display_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="max-w-4xl mx-auto p-0 sm:p-6 pb-24">
            <div className="sticky top-0 z-10 bg-[#0a0a0b] p-4 border-b border-gray-800 flex items-center justify-between lg:rounded-t-lg">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-gray-400 hover:text-white lg:hidden">
                        <ChevronLeft className="w-6 h-6" />
                    </button>
                    <h1 className="text-xl font-bold text-white">Mesajlar</h1>
                </div>
            </div>

            <div className="p-4 bg-[#1a1a1b]">
                <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                        placeholder="Kullanıcı ara..."
                        className="pl-9 bg-[#2a2a2b] border-gray-700 text-white placeholder:text-gray-500"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {loading ? (
                    <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-snapp-green"></div>
                    </div>
                ) : filteredConversations.length > 0 ? (
                    <div className="space-y-2">
                        {filteredConversations.map((conv) => (
                            <Link to={`/messages/${conv.user.id}`} key={conv.user.id}>
                                <Card className="bg-[#2a2a2b] border-gray-800 hover:bg-[#3a3a3b] transition-colors cursor-pointer rounded-lg overflow-hidden">
                                    <CardContent className="p-4 flex items-center gap-4">
                                        <Avatar className="h-12 w-12 border-2 border-[#1a1a1b]">
                                            <AvatarImage src={conv.user.avatar_url} />
                                            <AvatarFallback className="bg-blue-600 text-white">
                                                {conv.user.username.substring(0, 2).toUpperCase()}
                                            </AvatarFallback>
                                        </Avatar>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex justify-between items-center mb-1">
                                                <h3 className="font-semibold text-white truncate">
                                                    {conv.user.display_name || conv.user.username}
                                                </h3>
                                                <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                                                    {formatDistanceToNow(new Date(conv.last_message.created_at), { addSuffix: true, locale: tr })}
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <p className={`text-sm truncate ${conv.unread_count > 0 ? 'text-white font-medium' : 'text-gray-400'}`}>
                                                    {conv.last_message.sender_id === conv.user.id ? '' : 'Sen: '}
                                                    {conv.last_message.content}
                                                </p>
                                                {conv.unread_count > 0 && (
                                                    <span className="ml-2 bg-snapp-green text-[#0a0a0b] text-xs font-bold px-2 py-0.5 rounded-full">
                                                        {conv.unread_count}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </Link>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <div className="w-16 h-16 bg-[#2a2a2b] rounded-full flex items-center justify-center mx-auto mb-4">
                            <MessageCircle className="w-8 h-8 text-gray-500" />
                        </div>
                        <h3 className="text-lg font-medium text-white mb-2">Henüz mesajın yok</h3>
                        <p className="text-gray-400 max-w-sm mx-auto">
                            Takım arkadaşlarınla veya diğer kullanıcılarla iletişime geçmek için profillerindeki mesaj gönder butonunu kullanabilirsin.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Messages;
